// AlarmsPage — kõikide parkide alarmide ülevaade.
// Grupeeritud park-haaval, severity värvikoodid, lihtne ja loetav.
// Backend (AlarmTracker) annab iga alarmi `first_seen_at` aja (millal alarm tekkis),
// mida kuvame eraldi veerus.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import { AlertTriangle, AlertOctagon, Info, CheckCircle2, Activity, Droplets, Battery } from 'lucide-react';

// Goodwe MT/SMT error word bits (Table 8-2 — vt protokoll PDF)
// Kasutatakse GW50K-MT inverteri error_word dekodeerimiseks bitihaaval.
const GOODWE_ERROR_BITS = {
  0: 'GFCI Check Fail', 1: 'AC HCT Check Fail', 2: 'Ref 1.5V Fail',
  3: 'DC Switch Fail', 4: 'DC SPD Fail',
  6: 'GFCI Fail', 7: 'Relay Fail', 8: 'AC HCT Fail',
  9: 'Utility Loss', 10: 'Ground I Fail', 11: 'DC Bus High',
  12: 'Internal Fan Fail', 13: 'Over temperature',
  15: 'PV Over Voltage', 16: 'External Fan Fail',
  17: 'Vac Fail', 18: 'Isolation Fail', 19: 'High DC component',
  20: 'LCD Comm Fail', 21: 'String Over Current', 22: 'PV Reverse',
  23: 'Export Limit HW Fault', 24: 'N-PE Fail',
  25: 'Relay Check Fail', 26: 'L-PE Short Circuit',
  27: 'Night SPS Fail', 28: 'AFCI Fault',
  29: 'Grid frequency overrun', 30: 'EEPROM R/W Fail', 31: 'SPI Fail',
};

function decodeErrorBits(word, bitMap = GOODWE_ERROR_BITS) {
  const out = [];
  if (!word) return out;
  for (let bit = 0; bit < 32; bit++) {
    if (word & (1 << bit)) {
      out.push({ bit, name: bitMap[bit] || `Bit ${bit} (tundmatu)` });
    }
  }
  return out;
}

const SEVERITY = {
  critical: { label: 'Kriitiline', color: '#b91c1c', bg: 'rgba(220,38,38,0.06)' },
  warning:  { label: 'Hoiatus',    color: '#b45309', bg: 'rgba(180,83,9,0.06)'  },
  info:     { label: 'Info',       color: '#1d4ed8', bg: 'rgba(29,78,216,0.05)' },
};

function SourceBadge({ source }) {
  const map = {
    'BAU fault':       { c: '#dc2626', label: 'BAU fault' },
    'BAU L3':          { c: '#dc2626', label: 'BAU L3' },
    'BAU L2':          { c: '#b45309', label: 'BAU L2' },
    'BAU L1':          { c: '#1d4ed8', label: 'BAU L1' },
    'Vedelikjahutus':  { c: '#0891b2', label: 'Vedelikjahutus' },
    'Ohutus':          { c: '#dc2626', label: 'Ohutus' },
    'PCS':             { c: '#7c3aed', label: 'PCS' },
  };
  const m = map[source] || { c: 'var(--text-3)', label: source };
  return (
    <span style={{
      fontSize: '0.6rem', fontFamily: 'JetBrains Mono, monospace',
      fontWeight: 500, padding: '1px 6px', borderRadius: 3,
      border: `1px solid ${m.c}40`, color: m.c, background: m.c + '0d',
    }}>
      {m.label}
    </span>
  );
}

// Sama slug-funktsioon nagu backendis AlarmGenerator.js — tagab et frontend ja backend
// arvutavad sama `key` igale alarmile (slot:source:name).
const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const alarmKey = (slot, source, name) => `${slot}:${slug(source)}:${slug(name)}`;

function gatherParkAlarms(p) {
  const list = [];

  // Ühenduse-puudumise alarmid (per seade)
  // Kolm olukorda:
  //   1. Seadme andmed olemas, aga vanad (> 60s) → "ühendus puudub"
  //   2. Seadme host konfis, aga andmeid kunagi pole tulnud → "ühendus puudub" (suurem severity)
  //   3. Seadme host pole konfis → vaikselt jätta (pole monitooritav)
  const fmtTimeFromIso = (iso) => {
    try { return new Date(iso).toLocaleTimeString('et-EE'); } catch { return '—'; }
  };
  // Andmete kogumise ajaks võtame andme `_updated_at` (mõõdetud); kui pole, kasutame
  // praegust kellaaega (alarm on käesoleval pollil aktiivne).
  const nowIso = new Date().toISOString();
  const tsOf = (...candidates) => {
    for (const c of candidates) if (c) return c;
    return nowIso;
  };

  // Markedroidi-üleandmine: pcs_polling_disabled = EMS ei poll ex9EMS gateway'd
  // (PCS + grid_meter + pv_meter). Sel juhul "ühendamata" pole viga vaid kavatsuslik.
  const ex9emsHandover = p.control?.pcs_polling_disabled === true;
  const inverterHandover = p.control?.inverter_polling_disabled === true;

  if (ex9emsHandover) {
    list.push({
      severity: 'info',
      name: 'ex9EMS Modbus pollimine välja lülitatud (Markedroidi-üleandmine)',
      source: 'Info',
      meta: p.control?.markedroid_mqtt_host
        ? `Markedroid MQTT: ${p.control.markedroid_mqtt_host}`
        : 'markedroid_mqtt_host pole veel seatud',
      ts: nowIso,
    });
  }

  // Päikesejaama mõõdik (slave 1 @ park.host) — skipi kui Markedroidi-üleandmine
  if (p.host && !ex9emsHandover) {
    if (!p.solar) {
      list.push({ severity: 'critical', name: 'Päikesejaama mõõdik ühendamata (andmeid pole)',
        source: 'Päikesejaam', meta: `${p.host} slave ${p.slave_solar || 1}`, ts: nowIso });
    } else if (p.solar._online === false) {
      list.push({ severity: 'warning', name: 'Päikesejaama mõõdik ühendus katkenud',
        source: 'Päikesejaam', meta: `viimati: ${fmtTimeFromIso(p.solar._updated_at)} · ${p.host}`,
        ts: tsOf(p.solar._updated_at) });
    }
    if (!p.grid) {
      list.push({ severity: 'critical', name: 'GRID mõõdik ühendamata (andmeid pole)',
        source: 'GRID', meta: `${p.host} slave ${p.slave_grid || 2}`, ts: nowIso });
    } else if (p.grid._online === false) {
      list.push({ severity: 'warning', name: 'GRID mõõdik ühendus katkenud',
        source: 'GRID', meta: `viimati: ${fmtTimeFromIso(p.grid._updated_at)} · ${p.host}`,
        ts: tsOf(p.grid._updated_at) });
    }
  }

  // PCS — skipi kui Markedroidi-üleandmine
  if (p.pcs_host && !ex9emsHandover) {
    if (!p.pcs) {
      list.push({ severity: 'critical', name: 'PCS ühendamata (andmeid pole)',
        source: 'PCS', meta: p.pcs_host, ts: nowIso });
    } else if (p.pcs._online === false) {
      list.push({ severity: 'warning', name: 'PCS ühendus katkenud',
        source: 'PCS', meta: `viimati: ${fmtTimeFromIso(p.pcs._updated_at)} · ${p.pcs_host}`,
        ts: tsOf(p.pcs._updated_at) });
    }
  }

  // BAU — ainult kui konfis (bau_host olemas)
  if (p.bau_host) {
    if (!p.bau) {
      list.push({ severity: 'critical', name: 'BAU ühendamata (andmeid pole)',
        source: 'BAU', meta: p.bau_host, ts: nowIso });
    } else if (p.bau._online === false) {
      list.push({ severity: 'warning', name: 'BAU ühendus katkenud',
        source: 'BAU', meta: `viimati: ${fmtTimeFromIso(p.bau._updated_at)} · ${p.bau_host}`,
        ts: tsOf(p.bau._updated_at) });
    }
  }

  // Inverterite alarmid (per inverter parki sees) — skipi kui inverter_polling_disabled
  if (inverterHandover && Array.isArray(p.inverters) && p.inverters.length > 0) {
    list.push({
      severity: 'info',
      name: 'Inverter Modbus pollimine välja lülitatud (Markedroidi-üleandmine)',
      source: 'Info',
      meta: `${p.inverters.length} inverter${p.inverters.length === 1 ? '' : 'it'}`,
      ts: nowIso,
    });
  }
  if (Array.isArray(p.inverters) && !inverterHandover) {
    for (const inv of p.inverters) {
      const invName = `Inverter ${inv.type}` + (inv.label ? ` (${inv.label})` : '');
      const invMeta = `${inv.host}:${inv.slave_id}`;

      // 1) Ühenduse staatus
      if (inv._online === false) {
        list.push({
          severity: 'warning',
          name: `${invName} ühendus katkenud`,
          source: 'Inverter',
          meta: `viimati: ${fmtTimeFromIso(inv._updated_at)} · ${invMeta}`,
          ts: tsOf(inv._updated_at),
        });
      } else if (!inv._updated_at) {
        list.push({
          severity: 'critical',
          name: `${invName} ühendamata (andmeid pole)`,
          source: 'Inverter',
          meta: invMeta,
          ts: nowIso,
        });
      }

      // Edasised alarmid ainult kui andmed olemas
      if (inv._updated_at) {
        // 2) Work Mode = Fault (2)
        if (inv.work_mode === 2) {
          list.push({
            severity: 'critical',
            name: `${invName} viga (Fault mode)`,
            source: 'Inverter',
            meta: `${invMeta} · work_mode=${inv.work_mode}`,
            ts: tsOf(inv._updated_at),
          });
        }

        // 3) Error word — 32-bit, iga bitt eraldi alarm
        if (inv.error_word) {
          const bits = decodeErrorBits(inv.error_word);
          for (const b of bits) {
            list.push({
              severity: 'critical',
              name: `${invName} · ${b.name}`,
              source: 'Inverter',
              meta: `${invMeta} · bit ${b.bit}`,
              ts: tsOf(inv._updated_at),
            });
          }
        }

        // 4) Warning code
        if (inv.warning_code) {
          list.push({
            severity: 'warning',
            name: `${invName} warning code ${inv.warning_code}`,
            source: 'Inverter',
            meta: invMeta,
            ts: tsOf(inv._updated_at),
          });
        }

        // 5) Power Limit aktiivne (function_bits bit 3)
        if (inv.power_limit_active) {
          list.push({
            severity: 'info',
            name: `${invName} Power Limit aktiivne`,
            source: 'Inverter',
            meta: `${invMeta} · function_bits=0x${(inv.function_bits || 0).toString(16)}`,
            ts: tsOf(inv._updated_at),
          });
        }
      }
    }
  }

  // BAU bittide tasandi alarmid
  if (p.bau) {
    const bauTs = tsOf(p.bau._updated_at);
    if (Array.isArray(p.bau.active_alarms)) {
      for (const a of p.bau.active_alarms) {
        list.push({
          severity: a.severity,
          name: a.name,
          source: a.source === 'fault' ? 'BAU fault' : `BAU L${a.level}`,
          meta: `bit ${a.bit}`,
          ts: bauTs,
        });
      }
    }
    if (Array.isArray(p.bau.lc_active_faults)) {
      for (const f of p.bau.lc_active_faults) {
        list.push({
          severity: 'warning',
          name: f.name,
          source: 'Vedelikjahutus',
          meta: `${f.addr} = ${f.value}`,
          ts: bauTs,
        });
      }
    }
    if (p.bau.tempAndSmokeAlarm) {
      list.push({
        severity: 'critical',
        name: 'Suitsu/temp alarm aktiivne',
        source: 'Ohutus',
        meta: 'TempAndSmokeAlarm',
        ts: bauTs,
      });
    }
  }

  // Vana taandlik check — pcs_host olemas aga pcs_online=false (kui pcs objekt puudub)
  // Skipi kui Markedroidi-üleandmine
  if (p.pcs_host && !p.pcs_online && !p.pcs && !ex9emsHandover) {
    list.push({
      severity: 'warning',
      name: 'PCS ei vasta päringutele',
      source: 'PCS',
      meta: p.pcs_host,
      ts: nowIso,
    });
  }
  const order = { critical: 0, warning: 1, info: 2 };
  list.sort((a, b) => (order[a.severity] || 99) - (order[b.severity] || 99));
  return list;
}

function Pill({ children, color, bg, border }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 9px', borderRadius: 3,
      background: bg, border: `1px solid ${border}`,
      color, fontSize: '0.66rem', fontWeight: 500,
    }}>
      {children}
    </span>
  );
}

function fmtDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('et-EE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return '—'; }
}

function ParkSection({ park, alarms, firstSeenMap }) {
  const counts = {
    critical: alarms.filter(a => a.severity === 'critical').length,
    warning:  alarms.filter(a => a.severity === 'warning').length,
    info:     alarms.filter(a => a.severity === 'info').length,
  };
  const allOk = alarms.length === 0;
  const cellTempLow = park.bau?.tcell_min != null && park.bau.tcell_min < 5;

  return (
    <div id={`slot-${park.slot}`} style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 6, marginBottom: 14, overflow: 'hidden',
      scrollMarginTop: 72,
    }}>
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--divider)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'var(--surface-2)', flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text)' }}>
            {park.name || `Slot ${park.slot}`}
          </span>
          <span style={{ fontSize: '0.66rem', color: 'var(--text-4)', fontFamily: 'JetBrains Mono, monospace' }}>
            slot {park.slot} · {park.host}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {allOk ? (
            <Pill color="var(--positive)" bg="rgba(21,128,61,0.08)" border="rgba(21,128,61,0.25)">
              <CheckCircle2 size={11} /> Kõik OK
            </Pill>
          ) : (
            <>
              {counts.critical > 0 && (
                <Pill color="#b91c1c" bg="rgba(220,38,38,0.1)" border="rgba(220,38,38,0.3)">
                  <AlertOctagon size={11} /> {counts.critical} kriitilist
                </Pill>
              )}
              {counts.warning > 0 && (
                <Pill color="#b45309" bg="rgba(180,83,9,0.1)" border="rgba(180,83,9,0.3)">
                  <AlertTriangle size={11} /> {counts.warning} hoiatust
                </Pill>
              )}
              {counts.info > 0 && (
                <Pill color="#1d4ed8" bg="rgba(29,78,216,0.08)" border="rgba(29,78,216,0.25)">
                  <Info size={11} /> {counts.info} infot
                </Pill>
              )}
            </>
          )}
        </div>
      </div>

      {/* Pargi olek (vihjed) */}
      <div style={{
        display: 'flex', gap: 14, padding: '6px 16px',
        background: 'var(--surface-2)', borderBottom: '1px solid var(--divider)',
        fontSize: '0.66rem', color: 'var(--text-3)', flexWrap: 'wrap',
      }}>
        {park.bau?.soc != null && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Battery size={11} /> SOC {park.bau.soc.toFixed(1)}%
          </span>
        )}
        {(park.bau?.tcell_min != null || park.bau?.tcell_max != null) && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: cellTempLow ? 'var(--info)' : 'var(--text-3)' }}>
            <Activity size={11} /> Cell T {park.bau.tcell_min?.toFixed(1) ?? '—'}…{park.bau.tcell_max?.toFixed(1) ?? '—'}°C
          </span>
        )}
        {park.bau?.heat_state ? (
          <span style={{ color: 'var(--warning)' }}>🔥 Küte sees</span>
        ) : null}
        {park.bau?.dehum_state ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--info)' }}>
            <Droplets size={11} /> Niiskuse-eemaldaja
          </span>
        ) : null}
      </div>

      {!allOk && (
        <div>
          {/* Veergude pealkirjad (peidetud mobiilis CSS-iga) */}
          <div className="alarm-row alarm-row-header" style={{
            padding: '6px 16px',
            background: 'var(--surface-2)',
            borderBottom: '1px solid var(--divider)',
            fontSize: '0.6rem', textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-4)',
            fontWeight: 600,
          }}>
            <div className="alarm-sev">Tase</div>
            <div className="alarm-name">Kirjeldus</div>
            <div className="alarm-source">Allikas</div>
            <div className="alarm-time">Tekkis</div>
            <div className="alarm-meta">Meta</div>
          </div>
          {alarms.map((a, i) => {
            const s = SEVERITY[a.severity] || SEVERITY.info;
            return (
              <div key={i} className="alarm-row" style={{
                padding: '10px 16px',
                borderBottom: i < alarms.length - 1 ? '1px solid var(--divider)' : 'none',
                background: s.bg,
              }}>
                <div className="alarm-sev" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: s.color,
                }}>
                  {a.severity === 'critical' ? <AlertOctagon size={12} /> :
                   a.severity === 'warning'  ? <AlertTriangle size={12} /> :
                   <Info size={12} />}
                  {s.label}
                </div>
                <div className="alarm-name" style={{ fontSize: '0.82rem', color: 'var(--text)', fontWeight: 500 }}>
                  {a.name}
                </div>
                <div className="alarm-source"><SourceBadge source={a.source} /></div>
                <span className="alarm-time" style={{
                  fontFamily: 'JetBrains Mono, monospace', fontSize: '0.66rem',
                  color: 'var(--text-3)', whiteSpace: 'nowrap',
                }} title="Alarmi tekkimise aeg">
                  {(() => {
                    const k = alarmKey(park.slot, a.source, a.name);
                    const fs = firstSeenMap?.[k];
                    if (fs) return fmtDateTime(fs);
                    return a.ts ? fmtDateTime(a.ts) : '—';
                  })()}
                </span>
                <span className="alarm-meta" style={{
                  fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem',
                  color: 'var(--text-4)',
                }}>
                  {a.meta}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SummaryBox({ label, value, color = '#18181b', mono = false }) {
  return (
    <div style={{ background: 'var(--surface)', padding: '12px 16px' }}>
      <div style={{
        fontSize: '0.6rem', color: 'var(--text-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', fontWeight: 500, marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.02em',
        color, fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
    </div>
  );
}

export function AlarmsPage({ wsConnected }) {
  const { data, loading } = useParks();
  const location = useLocation();
  const [firstSeenMap, setFirstSeenMap] = useState({});

  // Lae backendist alarmide first_seen_at info iga 5 sekundi jooksul
  useEffect(() => {
    let cancel = false;
    const fetchAlarms = async () => {
      try {
        const r = await fetch('/api/parks/alarms');
        if (!r.ok) return;
        const d = await r.json();
        if (cancel) return;
        const m = {};
        for (const a of d.alarms || []) {
          if (a.key && a.first_seen_at) m[a.key] = a.first_seen_at;
        }
        setFirstSeenMap(m);
      } catch (_) {}
    };
    fetchAlarms();
    const iv = setInterval(fetchAlarms, 5000);
    return () => { cancel = true; clearInterval(iv); };
  }, []);

  // Kui URL-il on hash (#slot-N), keri sinna kui andmed laetud
  useEffect(() => {
    if (!data || !location.hash) return;
    const id = location.hash.slice(1);  // ilma "#"
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.style.boxShadow = '0 0 0 2px var(--info)';
      setTimeout(() => { el.style.boxShadow = ''; }, 1800);
    }
  }, [data, location.hash]);

  // Näita parke mis on konfis defineeritud (name olemas) — sõltumata online-staatusest.
  // Kui kõik seadmed offline, näeme alarmide all "ühendamata" critical-tasemel.
  // Filtreeri välja disabled (Markedroidi vms) ja muted pargid — neid me ei taha globaalsel alarmide lehel
  const configuredParks = (data?.parks || []).filter(p => p.name && !p.disabled && !p.muted);
  const totalAlarms = configuredParks.reduce((acc, p) => acc + gatherParkAlarms(p).length, 0);
  const totalCritical = configuredParks.reduce(
    (acc, p) => acc + gatherParkAlarms(p).filter(a => a.severity === 'critical').length, 0
  );

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header title="Alarmid" connected={wsConnected} />

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>
        {/* Üleüldine kokkuvõte */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1,
          marginBottom: 20,
          background: 'var(--border)', border: '1px solid var(--border)',
          borderRadius: 6, overflow: 'hidden',
        }}>
          <SummaryBox label="Kokku alarme" value={totalAlarms} color={totalAlarms === 0 ? '#15803d' : '#18181b'} />
          <SummaryBox label="Kriitilisi"   value={totalCritical} color={totalCritical > 0 ? '#b91c1c' : '#15803d'} />
          <SummaryBox label="Online parke" value={`${(data?.parks || []).filter(p => p.online).length}/${data?.parks?.length || 0}`} />
          <SummaryBox label="Uuendatud"    value={data?.updated ? new Date(data.updated).toLocaleTimeString('et-EE') : '—'} mono />
        </div>

        {loading && !data && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Laadin...</div>
        )}

        {!loading && configuredParks.length === 0 && (
          <div style={{
            padding: 40, textAlign: 'center', color: 'var(--text-3)',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
          }}>
            Ühtegi parki pole konfigureeritud.
          </div>
        )}

        {configuredParks.map((p) => (
          <ParkSection key={p.slot} park={p} alarms={gatherParkAlarms(p)} firstSeenMap={firstSeenMap} />
        ))}
      </div>
    </div>
  );
}

export default AlarmsPage;
